import Vue from 'vue';
import App from '@/App.vue';
import { installAuthInterceptor } from '@/services/authInterceptor';
import { registerCompetitorsDeep } from '@/services/competitorShirts';
import { b2bAuthenticate } from '@/services/b2bAuthService';
import { startB2bProxySession, clearB2bProxySession } from '@/services/b2bProxySession';
import { startB2bTokenWatcher, stopB2bTokenWatcher } from '@/services/b2bTokenWatcher';
import { postToParent, PARENT_EVENTS } from '@/services/parentBridge/parentBridge';
import { setBridgeTranslator } from '@/services/parentBridge/betPlacedEvent';
import { installParentCommands } from '@/services/parentBridge/parentCommands';
import { setTrustedParentOrigins } from '@/services/parentBridge/parentOrigins';
import { convertOddsByType } from '@/services/oddsFormat';
import { setKeyLiveConfig } from '@/services/keyLiveReapply.service';
import { installThemeSync, themeOrNull } from '@/services/themeSync';
import router from '@/router';
import store from '@/store';

import Vuetify from 'vuetify/lib';
import '@/plugins/vuetify-styles.less';

import en from 'vuetify/es5/locale/en';
import es from 'vuetify/es5/locale/es';
import fr from 'vuetify/es5/locale/fr';
import pt from 'vuetify/es5/locale/pt';
import it from 'vuetify/es5/locale/it';

import VueCryptojs from 'vue-cryptojs';
import UUID from 'vue-uuid';
import { i18n } from '@/plugins/vuei18n';
import '@babel/polyfill';
import axios from 'axios';
import VueI18n from 'vue-i18n';
import 'document-register-element/build/document-register-element';
import vueCustomElement from 'vue-custom-element';
import VueSocketio from 'vue-socket.io';
import VueMasonry from 'vue-masonry-css';
import io from 'socket.io-client';
import IdleVue from 'idle-vue';
import { buildTranslationOverrideMessages } from './utils/uiTranslationOverrides';

// Build marker: tells from the browser console which build is being served.
console.log('[revenge-betconsole] build 2026-10-30 17:40');

// Parent bridge: a rejected bet carries the text of the error modal, in the player's language
setBridgeTranslator((key, values) => i18n.t(key, values));

// ── Helpers ───────────────────────────────────────────────────────────────────

const el = document.querySelector('#revenge-bet-console');

function attr(name, fallback = null) {
    const val = el.getAttribute(name);
    return (val !== null && val !== '') ? val : fallback;
}

function addCss(fileName) {
    const link = document.createElement('link');
    link.type = 'text/css';
    link.rel  = 'stylesheet';
    link.href = fileName;
    document.head.appendChild(link);
}

// ── Window globals from host attributes ───────────────────────────────────────

window.skin            = attr('skin');
window.theme           = attr('theme');
window.icons           = window.theme === 'dark' ? 'light' : 'dark';
window.icarus          = false;
window.css             = attr('css');
window.openlivesection = attr('openlivesection', false);
window.openTree        = attr('openTree', false);
window.sportId         = attr('sportId');
window.tournamentId    = attr('tournamentId');
window.eventId         = attr('eventId');
window.sportName       = attr('sportName');
window.tournamentName  = attr('tournamentName');
window.eventName       = attr('eventName');
// page / categoryId: needed by outright links (page=antepost), whose ids are not prematch ids.
window.page            = attr('page');
window.categoryId      = attr('categoryId');
window.forceMobileView = attr('forceMobileView', false);

// Fallback: se gli attributi HTML non sono impostati (es. Nuxt2 SPA li setta
// dinamicamente dopo che lo script è già partito), leggi i parametri di deep-link
// direttamente dalla URL della pagina. Funziona quando il betconsole è embeddato
// come script nella stessa pagina (non come iframe separato).
// Nota: filtra le stringhe "null"/"undefined" che URLSearchParams produce quando
// il componente Nuxt2 (SportIframe) non ha ancora valorizzato i parametri.
try {
    const _up = new URLSearchParams(window.location.search);
    const _urlVal = (k) => { const v = _up.get(k); return (v && v !== 'null' && v !== 'undefined') ? v : null; };
    if (!window.sportId)        window.sportId        = _urlVal('sportId');
    if (!window.tournamentId)   window.tournamentId   = _urlVal('tournamentId');
    if (!window.eventId)        window.eventId        = _urlVal('eventId');
    if (!window.sportName)      window.sportName      = _urlVal('sportName');
    if (!window.tournamentName) window.tournamentName = _urlVal('tournamentName');
    if (!window.eventName)      window.eventName      = _urlVal('eventName');
    if (!window.page)           window.page           = _urlVal('page');
    if (!window.categoryId)     window.categoryId     = _urlVal('categoryId');
    if (!window.theme)          window.theme          = _urlVal('theme');
    if ((!window.openlivesection || window.openlivesection === false) && _up.get('isLive') === 'true') {
        window.openlivesection = 'true';
    }
    if (_up.get('openresults') === 'true') {
        window.openresults = true;
    }
} catch (_) {}
// Theme chosen by the player on the host platform: wins over the skin's default theme
const hostTheme = themeOrNull(window.theme);
if (hostTheme) store.commit('settings/SET_THEME', hostTheme);
window.operatorToken   = attr('token');
const loginPromptEnabled  = attr('login_prompt_enabled', 'true') !== 'false'; //todo funzionalità settabile loginmessage
const loginPromptDelaySec = parseInt(attr('login_prompt_delay', '60'), 10) || 60; //todo funzionalità settabile loginmessage

const lang        = attr('lang', process.env.VUE_APP_FALLBACK_LOCALE);
const playVal     = el.getAttribute('play');
const geoPlay     = playVal !== null ? playVal === 'true' : true;
const platform_id = attr('platform');
// user_id is `let` because B2B authentication (when enabled) overrides it with
// the userId returned by /b2b/authenticate before the bookietoken step.
let   user_id     = attr('user_id');
const icarus      = attr('icarus');
const operatorToken = window.operatorToken;

// ── CSS injection ─────────────────────────────────────────────────────────────

if (window.css) {
    const cssUrls = {
        local:       `http://localhost:8082/themes/skin/${window.css}.css`,
        development: `${process.env.VUE_APP_REVENGE_DEV}/themes/skin/${window.css}.css`,
        production:  `${process.env.VUE_APP_URL_SIRPLAY_CLOUDFRONT}/themes/skin/${window.css}.css`,
    };
    if (cssUrls[process.env.NODE_ENV]) addCss(cssUrls[process.env.NODE_ENV]);
}

if (window.forceMobileView === true || window.forceMobileView === 'true') {
    document.body.classList.add('mobile-view');
}

// ── Vue plugins ───────────────────────────────────────────────────────────────

Vue.use(VueCryptojs);
Vue.use(UUID);
Vue.use(Vuetify);
Vue.use(VueI18n);
Vue.use(vueCustomElement);
Vue.use(VueMasonry);

const eventsHub = new Vue();
const _idleMinutes = parseInt(process.env.VUE_APP_IDLE_TIMEOUT_MINUTES) || 30;
Vue.use(IdleVue, { eventEmitter: eventsHub, idleTime: _idleMinutes * 60 * 1000 });

Vue.config.productionTip = false;

// ── Filters ───────────────────────────────────────────────────────────────────

Vue.filter('round_digits', (value, digits) =>
    value ? value.toFixed(digits) : (0.00).toFixed(digits)
);

Vue.filter('truncate', (value, size, truncate = '...') => {
    if (!value) return '';
    const str = value.toString();
    return str.length <= size ? str : str.substr(0, size) + truncate;
});

Vue.filter('capitalize', (value) => {
    if (!value) return '';
    const str = value.toString();
    return str.charAt(0).toUpperCase() + str.slice(1);
});

Vue.filter('odds_type_filter', (value) =>
    convertOddsByType(value, store.getters['odds/getOddsType'])
);

// ── Vuetify config ────────────────────────────────────────────────────────────

const optsVuetify = {
    lang: { current: lang, silentFallbackWarn: true },
    theme: {
        options: { customProperties: true },
        dark: store.getters['settings/getTheme'] !== 'light',
        themes: {}
    }
};

// ── Skin settings ─────────────────────────────────────────────────────────────

function applySkinSettings(skin = {}) {
    const rules = Array.isArray(skin.rules) ? skin.rules : [];
    const settings = skin.settings ?? {};

    // Core rules & config
    store.commit('rules/SET_RULES_MANAGER', []);
    store.commit('rules/SET_RULES_SKIN', rules);
    store.commit('rules/SET_RULES_DISABLED', skin.disables ?? []);
    store.commit('rules/SET_TEMPLATE_MANAGER', []);
    store.commit('rules/SET_TEMPLATE_SKIN', skin.templates ?? []);
    store.commit('cart/SET_CART_SETTINGS', rules);
    store.commit('settings/SET_WEBSITE_URL', skin.website || '');
    // Parent bridge: the sites allowed to send commands and to receive the events (none → as before)
    setTrustedParentOrigins(skin.parentOrigins);

    // Settings
    store.commit('settings/SET_PLATFORM_SETTINGS', settings);
    store.commit('settings/SET_RESULTS', skin.results ?? []);
    store.commit('settings/SET_SHOW_QUICKBET', skin.showquickbet ?? false);
    store.commit('settings/SET_NEXT_EVENT', skin.nextevent ?? false);
    store.commit('settings/SET_EVENT_DAY', skin.eventday ?? false);
    store.commit('settings/SET_CASH_OUT', skin.cashout ?? skin.useCashout ?? false);
    store.commit('settings/SET_CASH_OUT_LIVE', skin.cashoutlive ?? false);
    store.commit('settings/SET_BET_FREEZE', skin.enableBetFreeze === true);
    store.commit('settings/SET_ICON', skin.icon ?? 'icons');
    store.commit('settings/SET_VOID_AS_LOSE', skin.voidAsLose ?? false);
    store.commit('settings/SET_STREAM_LIVE', settings.stream_live ?? false);
    store.commit('settings/SET_LIVE_PREVIEW_MAX_EVENTS', settings.livePreviewMaxEvents ?? 100);
    store.commit('settings/SET_LIVE_DETAIL_MAX_MARKETS', settings.liveDetailMaxMarkets ?? 500);
    store.commit('settings/SET_PREMATCH_PREVIEW_MAX_EVENTS', settings.prematchPreviewMaxEvents ?? 15);
    store.commit('settings/SET_PREMATCH_DETAIL_MAX_MARKETS', settings.prematchDetailMaxMarkets ?? 150);
    store.commit('settings/SET_MOST_PLAYED_MAX_EVENTS_FOR_PAGE', settings.mostPlayedMaxEventsForPage ?? 10);
    store.commit('settings/SET_NEXT_EVENTS_MAX_EVENTS_FOR_PAGE', settings.nextEventsMaxEventsForPage ?? 10);
    store.commit('settings/SET_STARTING_SOON_MAX_EVENTS_FOR_PAGE', settings.startingSoonMaxEventsForPage ?? 10);
    store.commit('settings/SET_LIVE_NOW_PREVIEW_MAX_EVENTS_FOR_PAGE', settings.liveNowPreviewMaxEventsForPage ?? 10);
    store.commit('settings/SET_MAX_MIXED_EVENTS_FOR_PAGE', settings.maxMixedEventsForPage ?? 50);
    store.commit('settings/SET_TIME_TO_WAIT_TO_CALL_TOURNAMENT_LIVE', settings.timeToWaitToCallTournamentLive ?? 5000);


    // shortCode will be committed later in initAppVue once cartObj is available

    // Odds type
    if (settings.typeOdds != null) {
        store.commit('odds/SET_ODDS_TYPE', settings.typeOdds || 'decimal');
    }
    if (settings.supportedOddsFormats != null) {
        store.commit('odds/SET_SUPPORTED_ODDS', settings.supportedOddsFormats || []);
    }

    // Feature flags
    store.commit('settings/SET_ENABLE_TAX_ON_WON', skin.enabledTaxOnWon ?? false);
    store.commit('settings/SET_PERCENTAGE_TAX_VALUE_ON_WON', skin.percentageTaxValueOnWon ?? 0);
    store.commit('settings/SET_AGENCY_CAN_PLAY_FOR_USER', skin.enableAgencyCanPlayForUser ?? false);
    store.commit('settings/SET_ENABLE_AGENCY_AUTO_RECHARGE', skin.enableAgencyAutoRecharge === true);
    store.commit('settings/SET_CASHIER_CAN_PLAY_FOR_USER', skin.enableCashierCanPlayForUser === true);
    store.commit('settings/SET_ENABLE_AGENCY_CASHOUT', skin.enableAgencyCashout ?? false);
    store.commit('settings/SET_ENABLE_CASHIER_CASHOUT', skin.enableCashierCashout ?? false);
    store.commit('settings/SET_ENABLE_TERMINAL_CASHOUT', skin.enableTerminalCashout ?? false);
    store.commit('settings/SET_ENABLE_MOBILE_BOTTOM_TOOLBAR', skin.enableMobileBottomToolbar ?? false);
    store.commit('settings/SET_EXPLODE_MARKETS_ON_MOBILE_DEVICE', skin.explodeMarketOnMobileDevice ?? false);
    store.commit('settings/SET_SHOW_MULTIPLICITY_LEGEND_ON_MOBILE_DEVICE', skin.showMultiplicityLegendMobile ?? false);
    store.commit('settings/SET_ENABLE_TREE_WITH_PLAYER_OUTCOMES', skin.enableTreeWithPlayerOutcomes ?? false);

    // Optional flags
    if (skin.featuredTournamentAutoLoad != null) {
        store.commit('settings/SET_FEATURED_TOURNAMENT_AUTOLOAD', skin.featuredTournamentAutoLoad);
    }
    if (skin.featuredTournamentWidget != null) {
        store.commit('settings/SET_FEATURED_TOURNAMENT_WIDGET', skin.featuredTournamentWidget);
    }
    if (skin.enableClearCartAtPlaceBet != null) {
        store.commit('settings/SET_ENABLE_CLEAR_CART_AT_PLACE_BET', skin.enableClearCartAtPlaceBet);
    }
    if (skin.quickbet != null) {
        store.commit('settings/SET_QUICK_BET_ENABLED', skin.quickbet);
    }
    if (skin.allowIntegralLiveBet != null) {
        store.commit('settings/SET_ALLOW_INTEGRAL_LIVE_BET', skin.allowIntegralLiveBet);
    }
    if (skin.cashoutApiPollIntervalSec != null) {
        store.commit('settings/SET_CASHOUT_POLL_INTERVAL_MS', skin.cashoutApiPollIntervalSec * 1000);
    }
    store.commit('settings/SET_ENABLE_EARLY_PAYOUT', skin.enableEarlyPayout ?? true);
    store.commit('settings/SET_ENABLE_MOST_PLAYED_WIDGET', skin.enableMostPlayedWidget ?? true);
    store.commit('settings/SET_ENABLE_NEXT_EVENTS_WIDGET', skin.enableNextEventsWidget ?? true);
    store.commit('settings/SET_ENABLE_STARTING_SOON_WIDGET', skin.enableStartingSoonWidget ?? true);
    // Feature flag for user navigation tracking — default false until explicitly enabled per platform
    store.commit('settings/SET_ENABLE_USER_TRACKING', skin.enableUserTracking ?? false);
    store.commit('settings/SET_ENABLE_BET_BUILDER', skin.enableBetBuilder ?? false);
    // Live bet builder: only DataSavage prices live events (rocky-bets refuses the rest).
    store.commit('settings/SET_ENABLE_LIVE_BET_BUILDER',
        skin.enableBetBuilder === true && skin.enableLiveBetBuilder === true && skin.betBuilderProvider === 'datasavage');
    store.commit('settings/SET_B2B_ENABLED', skin.enableB2b === true);
    store.commit('settings/SET_B2B_REFRESH_ENABLED', skin.enableB2BRefresh);

    // KEY live re-apply — commit to the settings module for reactive consumers
    // AND prime the shared service singleton so socket mutations can read the
    // config synchronously without touching the Vuex store on every message.
    const enableKeyEvalLive = skin.enableKeyEvaluationLive === true;
    const keyLoosenLimitPp  = skin.keyLoosenLimitPp ?? null;
    store.commit('settings/SET_ENABLE_KEY_EVALUATION_LIVE', enableKeyEvalLive);
    store.commit('settings/SET_KEY_LOOSEN_LIMIT_PP', keyLoosenLimitPp);
    setKeyLiveConfig({
        enableKeyEvaluationLive: enableKeyEvalLive,
        keyLoosenLimitPp,
    });

    const userChoice = localStorage.getItem('eventViewMode_user');
    store.commit('ui/SET_EVENT_VIEW_MODE', userChoice || skin.eventDisplayMode || 'compact');
    if (skin.logoUrl) {
        store.commit('settings/SET_SKIN_LOGO_URL', skin.logoUrl);
    }

}

// ── Login prompt (anonymous users only) ───────────────────────────────────────
// Fires OPEN_LOGIN once per tab session after a configurable delay.
// sessionStorage ensures it never fires twice even if the Vue app is remounted.
//todo funzionalità settabile loginmessage

function scheduleLoginPrompt(delaySec) {
    const KEY = '_rbc_login_prompt_sent';
    if (sessionStorage.getItem(KEY)) return;
    setTimeout(() => {
        if (sessionStorage.getItem(KEY)) return;
        sessionStorage.setItem(KEY, '1');
        window.parent.postMessage({ type: 'OPEN_LOGIN' }, '*');
    }, delaySec * 1000);
}

// ── Electron detection ────────────────────────────────────────────────────────

function detectElectron() {
    return (
        (typeof window    !== 'undefined' && typeof window.process    === 'object' && window.process.type === 'renderer') ||
        (typeof process   !== 'undefined' && typeof process.versions  === 'object' && !!process.versions.electron) ||
        (typeof navigator === 'object'    && navigator.userAgent.indexOf('Electron') >= 0)
    );
}

// ── Vue config ────────────────────────────────────────────────────────────────

function configureVue() {
    Vue.config.silent         = true;
    Vue.config.productionTip  = false;
    Vue.config.devtools       = process.env.NODE_ENV === 'local';

    // Auto-enable KEY debug flags in non-production builds. In prod (`build`
    // script) NODE_ENV === 'production' so the flags stay untouched (i.e.
    // undefined → falsy) and both the console-log firehose and the visible
    // Inspector button are excluded from the DOM. Manually setting the flag
    // in DevTools still works — this only sets a default, doesn't lock it.
    if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
        if (window.__DEBUG_KEY_UI === undefined) window.__DEBUG_KEY_UI = true;
        if (window.__DEBUG_KEY_LIVE_REAPPLY === undefined) window.__DEBUG_KEY_LIVE_REAPPLY = true;
    }

}

// ── Vue mount helper ──────────────────────────────────────────────────────────
// Centralized to mount the app even on error routes
// (e.g., /e/100, /e/300) where the normal flow terminates before mounting.

function mountVue() {
    configureVue();
    const root = new Vue({
        router,
        i18n,
        axios,
        store,
        vuetify: new Vuetify(optsVuetify),
        render: h => h(App)
    }).$mount('#revenge-bet-console');
    // Parent bridge: the embedding page may remove a selection or clear the slip
    installParentCommands({ store, root });
    // Theme: the store's, now and on every change (SET_THEME from the host included)
    installThemeSync({ store, root });
}

// ── Auth & Bootstrap ──────────────────────────────────────────────────────────

const AUTH_URL    = process.env.VUE_APP_URL_API + '/auth/livetoken';
const REFRESH_URL = process.env.VUE_APP_URL_API + '/auth/tokenguest';

// ── Axios 401 interceptor — auto token refresh ────────────────────────────────

installAuthInterceptor({
    axios,
    store,
    router,
    refreshUrl:        REFRESH_URL,
    VUE_APP_URL_API:   process.env.VUE_APP_URL_API,
    VUE_APP_URL_RULES: process.env.VUE_APP_URL_RULES,
    platformId:        platform_id,
    userId:            user_id,
});

// Shirt colours of the competitors (competitors[].shirtColor*) are collected from
// every API response, so LogoCompetitors draws the coloured shirt in all widgets.
axios.interceptors.response.use((response) => {
    try {
        registerCompetitorsDeep(response && response.data);
    } catch (e) {
        // Never break a response because of the shirt colours.
    }
    return response;
});

// Attach the logged-in user token to every request when user tracking is enabled.
// The feature flag `enableUserTracking` comes from the skin config — setting it to
// false in the platform config instantly disables the header without any code deploy.
axios.interceptors.request.use((config) => {
    try {
        const trackingEnabled = store.getters['settings/getEnableUserTracking'];
        if (!trackingEnabled) return config;

        const userToken = localStorage.getItem('logged_token');

        if (!userToken) return config;

        const apiUrl = process.env.VUE_APP_URL_API || '';
        const proxyUrl = store.getters['settings/getProxyUrl'] || window.skin?.proxyUrl || '';

        if (!config || !config.url) return config;

        const requestUrl = config.url.startsWith('http')
            ? config.url
            : `${config.baseURL || ''}${config.url}`;

        const isGoingToApi = apiUrl && requestUrl.startsWith(apiUrl);
        const isGoingToProxy = proxyUrl && requestUrl.startsWith(proxyUrl);

        if (isGoingToApi) {
            config.headers = config.headers || {};
            config.headers['X-User-Authorization'] = `Bearer ${userToken}`;
        }
    } catch (error) {
        console.warn('[Axios Request Interceptor] Impossibile iniettare X-User-Authorization:', error.message);
    }

    return config;
});

if (!localStorage.getItem('token')) {
    axios.post(AUTH_URL, { platform: platform_id })
        .then(({ data }) => {
            localStorage.setItem('token',   data.accessToken);
            localStorage.setItem('refresh', data.refreshToken);
            localStorage.setItem('expired', data.expired);
            localStorage.setItem('scope',   data.scope);
            initAppVue(data.accessToken);
        })
        .catch(error => {
            initAppVue('not_auth');
        });
} else {
    const expired  = localStorage.getItem('expired');
    const diffTime = new Date(expired * 1000) - Date.now();

    if (diffTime < 0) {
        axios.post(REFRESH_URL, { token: localStorage.getItem('refresh') })
            .then(({ data }) => {
                localStorage.setItem('token',   data.accessToken);
                localStorage.setItem('expired', data.expired);
            })
            .then(() => initAppVue(localStorage.getItem('token')))
            .catch(error => {
                console.log('error in urlrefresh', error);
                initAppVue('not_auth');
            });
    } else {
        initAppVue(localStorage.getItem('token'));
    }
}

// ── App initialization ────────────────────────────────────────────────────────

async function initAppVue(token) {
    if (token === 'not_auth') {
        sessionStorage.setItem('_lastAuthErrorStatus', '401');
        router.push('/e/100');
        mountVue();
        return;
    }

    // Tracks bookietoken failure without a top-level return (the .catch is
    // inside a `.then().catch()` chain, so return from the callback wouldn't
    // exit initAppVue). Used later to skip the counter reset that would
    // otherwise cause an infinite reload loop when auth fails permanently.
    let bookietokenFailed = false;

    store.dispatch('settings/setToken', token);

    const configUrl = process.env.VUE_APP_URL_API + '/v3/skins-config/platform-config';
    const uiPersonalizationUrl = process.env.VUE_APP_URL_API + `/v3/ui-personalization/${platform_id}`;
    const uiTranslationsUrl = process.env.VUE_APP_URL_API + `/v3/ui-translations/${platform_id}/${i18n.locale}`;
    const badgeIconsUrl = process.env.VUE_APP_URL_API + `/v3/badge-icons/${platform_id}`;

    // Per-platform translation overrides (admin-configured via rocky-admin).
    // Deliberately NOT part of the Promise.all below: this call must never be
    // able to delay mount. It's fired in parallel and merged in whenever it
    // resolves — vue-i18n messages are reactive, so an override that lands
    // after mount still updates the rendered strings instantly. Empty/missing
    // overrides are a no-op merge, so bundled i18n strings in src/i18n/*.json
    // remain the winning value unless explicitly overridden.
    axios.get(uiTranslationsUrl, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
            const translationOverrides = res?.data?.translations;
            if (translationOverrides && typeof translationOverrides === 'object' && Object.keys(translationOverrides).length > 0) {
                // Keys are dot paths to a single string ("errors.sign"): each one becomes a
                // nested object holding only that leaf, so the merge changes that string
                // and nothing else. Paths ending on a whole group (generic, bets, errors,
                // cashout, ...) are rejected — merging a string there would replace the
                // group and turn all its $t() calls into raw keys.
                const { messages, rejected } = buildTranslationOverrideMessages(
                    translationOverrides,
                    i18n.getLocaleMessage(i18n.locale),
                    i18n.getLocaleMessage(i18n.fallbackLocale)
                );
                if (rejected.length > 0) {
                    console.warn('[initAppVue] Ignored uiTranslation overrides (not a single string):', rejected.join(', '));
                }
                if (Object.keys(messages).length > 0) {
                    i18n.mergeLocaleMessage(i18n.locale, messages);
                }
            }
        })
        .catch((err) => {
            // Translation overrides are best-effort: never block or fail boot on this call.
            console.error('[initAppVue] Cannot fetch uiTranslations:', err.message);
        });

    // Per-platform badge overrides (early payout / cashout — custom text per
    // language, on/off toggle, adjustable pixel size), admin-configured via
    // rocky-admin. Same decoupled, non-blocking pattern as uiTranslationsUrl
    // above: fired in parallel, never part of the Promise.all below, so a
    // slow/failed response never delays or fails boot. FeatureBadge.vue reads
    // the merged config reactively via settings/getBadgeIconConfig, so a
    // response landing after mount still updates the rendered badges.
    axios.get(badgeIconsUrl, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
            const badgeIconsConfig = res?.data?.badges;
            if (badgeIconsConfig && typeof badgeIconsConfig === 'object') {
                store.commit('settings/SET_BADGE_ICONS_CONFIG', badgeIconsConfig);
            }
        })
        .catch((err) => {
            // Badge overrides are best-effort: never block or fail boot on this call.
            console.error('[initAppVue] Cannot fetch badgeIcons:', err.message);
        });

    Promise.all([
        axios.post(configUrl, {}, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(uiPersonalizationUrl, { headers: { Authorization: `Bearer ${token}` } })
            .catch((err) => {
                // Removed silent catch, added proper error logging
                console.error('[initAppVue] Cannot fetch uiPersonalization:', err.message);
                return null;
            }),
    ]).then(async ([configResult, uiPersonalizationResult]) => {
        const { data } = configResult;
        const skin = data.data.skin;

        applySkinSettings(skin);

        // Admin-controlled debug inspectors (KEY + FREEZE).
        //
        // The two overlays (see components/debug/KeyInspector.vue and
        // FreezeInspector.vue) are gated by `window.__DEBUG_KEY_UI` and
        // `window.__DEBUG_FREEZE_UI` respectively. Historically these
        // flags were only settable via DevTools console for on-the-fly
        // debugging. Post-2026-07-24 the admin can flip them for a
        // specific platform + a whitelist of userIds via SkinConfig:
        //
        //   skin.debugInspectorsEnabled: Boolean
        //   skin.debugInspectorsUserIds: [String]
        //
        // Precedence: BOTH conditions must hold. Empty whitelist with
        // `enabled=true` reveals to nobody (safe default). If the admin
        // adds our userId, the overlays appear next to each market
        // header once the platform config lands.
        //
        // Compare uses String(user_id) — the whitelist stores strings
        // because the SkinConfig schema stores them as `[String]`, and
        // the JWT `userId` claim can arrive as either a number or string
        // depending on the login flow.
        const debugEnabled   = skin.debugInspectorsEnabled === true;
        const debugWhitelist = Array.isArray(skin.debugInspectorsUserIds) ? skin.debugInspectorsUserIds : [];
        const debugActive    = debugEnabled && debugWhitelist.map(String).includes(String(user_id));
        if (debugActive) {
            window.__DEBUG_KEY_UI    = true;
            window.__DEBUG_FREEZE_UI = true;
        } else {
            // Belt-and-braces: if the admin removed us from the whitelist
            // between a previous session and this one, ensure the flags
            // aren't lingering from the old browser tab state.
            delete window.__DEBUG_KEY_UI;
            delete window.__DEBUG_FREEZE_UI;
        }

        if (skin.enableBetBuilder === true) {
            axios.get(
                process.env.VUE_APP_URL_API + '/v3/bet-builder/bootstrap/slim',
                { headers: { Authorization: `Bearer ${token}` } }
            ).then(({ data }) => {
                if (data?.data) store.dispatch('betbuilder/loadBootstrap', data.data);
            }).catch(err => {
                console.error('[betBuilder] bootstrap error:', err.message);
            });
        }

        if (uiPersonalizationResult?.data?.data?.left) {
            store.commit('settings/SET_UI_PERSONALIZATION_LEFT', uiPersonalizationResult.data.data.left);
        }

        if (uiPersonalizationResult?.data?.data?.center) {
            store.commit('settings/SET_UI_PERSONALIZATION_CENTER', uiPersonalizationResult.data.data.center);
        }

        if (uiPersonalizationResult?.data?.data?.right) {
            store.commit('settings/SET_UI_PERSONALIZATION_RIGHT', uiPersonalizationResult.data.data.right);
        }

        if (uiPersonalizationResult?.data?.data?.general) {
            store.commit('settings/SET_UI_PERSONALIZATION_GENERAL', uiPersonalizationResult.data.data.general);
        }

        if (uiPersonalizationResult?.data?.data?.timeFilterSteps) {
            store.commit('settings/SET_TIME_FILTER_STEPS', uiPersonalizationResult.data.data.timeFilterSteps);
        }

        store.commit('settings/SET_GAME_CONSOLE', uiPersonalizationResult.data.data.sportModule ?? 'marketview');
        const _theme = hostTheme || (uiPersonalizationResult?.data?.data?.theme === 'light' ? 'light' : 'dark');
        const _chooseThemeMode = uiPersonalizationResult?.data?.data?.chooseThemeMode === true;
        // Applied to Vuetify and to <html class="light"> by installThemeSync
        store.commit('settings/SET_THEME', _theme);
        store.commit('settings/SET_CHOOSE_THEME_MODE', _chooseThemeMode);

        const _layoutFont = uiPersonalizationResult?.data?.data?.layoutFont;
        if (_layoutFont === 'extralarge') {
            document.documentElement.classList.add('xl');
        } else if (_layoutFont === 'large') {
            document.documentElement.classList.add('large');
        }

        if(uiPersonalizationResult?.data?.data?.competitorsNameInMarket) {
            store.commit('settings/SET_COMPETITORS_IN_OUTCOME', uiPersonalizationResult?.data?.data?.competitorsNameInMarket ?? false);
        }

        if(uiPersonalizationResult?.data?.data?.dynamicMarketToView) {
            store.commit('settings/SET_DYNAMIC_MARKET_TO_VIEW', uiPersonalizationResult?.data?.data?.dynamicMarketToView ?? false);
        }

        if(uiPersonalizationResult?.data?.data?.colToShowInDetail) {
            store.commit('settings/SET_COL_TO_SHOW_IN_DETAIL', uiPersonalizationResult?.data?.data?.colToShowInDetail ?? 2);
        }

        if(uiPersonalizationResult?.data?.data?.showSirplayLicence) {
            store.commit('settings/SET_SHOW_SIRPLAY_LICENCE', uiPersonalizationResult?.data?.data?.showSirplayLicence ?? true);
        }


        const _generalItems = uiPersonalizationResult?.data?.data?.general ?? [];
        const _keypadItem = _generalItems.find(item => item?.id === 'mobile_keypad' && item?.enabled === true);
        if (_keypadItem?.config?.type) {
            store.commit('settings/SET_MOBILE_KEYPAD_TYPE', _keypadItem.config.type);
        }

        const _participantLogosItem = _generalItems.find(item => item?.id === 'participant_logos');
        const _tournamentLogosItem = _generalItems.find(item => item?.id === 'tournament_logos');
        store.commit('settings/SET_SHOW_PARTICIPANT_LOGOS', _participantLogosItem?.enabled !== false);
        store.commit('settings/SET_SHOW_TOURNAMENT_LOGOS', _tournamentLogosItem?.enabled !== false);
        store.commit('settings/SET_COMPETITORS_NAME_IN_MARKET', uiPersonalizationResult?.data?.data?.competitorsNameInMarket === true);

        // Live polling interval in ms (default 30s). Handled by individual live components.
        const MIN_LIVE_POLLING_MS = 30000;
        const livePollingMs = Math.max(
            MIN_LIVE_POLLING_MS,
            skin.settings?.livePollingIntervalMs || skin.livePollingIntervalMs || 30000
        );
        store.commit('settings/SET_LIVE_POLLING_INTERVAL_MS', livePollingMs);
        const finishedEventRemovalMs = skin.settings?.finishedEventRemovalMs || 300000;
        store.commit('settings/SET_FINISHED_EVENT_REMOVAL_MS', finishedEventRemovalMs);

        store.commit('settings/SET_LIVE_SCORE_POLLING_INTERVAL_MS', skin.settings?.liveScorePollingIntervalMs);
        store.commit('settings/SET_LIVE_DETAIL_ODDS_REFRESH_INTERVAL_MS', skin.settings?.liveDetailOddsRefreshIntervalMs);

        // data.data.cart already has scope:'cart', type:'cart', source:'betconsole' — pass it directly.
        // data.data.cartRules contains cart_bonus rules; they only need scope:'cart' added.
        const cartObj = data.data.cart;
        const cartRules = (data.data.cartRules ?? []).map(r => ({ ...r, scope: 'cart' }));
        const extraRules = [...(cartObj ? [cartObj] : []), ...cartRules];
        if (extraRules.length > 0) {
            store.commit('cart/SET_CART_SETTINGS', extraRules);
        }

        store.commit('settings/SET_ENABLE_QUICK_BET_ONLY_FOR_AGENCY', cartObj.detail.enableQuickBetOnlyForAgency ?? false);


        // shortCode: prefer cart.detail.shortCode, fallback to skin.betCodeViewSelector === 'short'
        const shortCode = cartObj?.detail?.shortCode ?? (skin.betCodeViewSelector === 'short') ?? false;
        store.commit('settings/SET_SHORT_CODE', shortCode);

        // Proxy URL. VUE_APP_IGNORE_SKIN_PROXY=true (local development only) keeps
        // the calls on VUE_APP_BET_API_URL, so a local rocky-bets can be tested on a
        // skin that goes through a remote proxy in its real environments.
        const proxyUrl = process.env.VUE_APP_IGNORE_SKIN_PROXY === 'true' ? null : (skin.proxyUrl || null);
        if (proxyUrl) store.dispatch('settings/setProxyUrl', proxyUrl);

        // Banners
        const _bannersBase = proxyUrl
            ? `${proxyUrl}/sircms/banners`
            : `${skin.settings.sircms_endpoint}bookie/sircms/banners`;
        axios.get(`${_bannersBase}?language=${lang}`, { headers: { 'content-type': 'application/json' } })
            .then(result => {
                const banners = result.data.success;
                if (banners && banners.length > 0) {
                    store.commit('settings/SET_BANNERS', banners);
                } else if (lang !== 'en') {
                    axios.get(`${_bannersBase}?language=en`, { headers: { 'content-type': 'application/json' } })
                        .then(fallback => store.commit('settings/SET_BANNERS', fallback.data.success));
                }
            });

        store.commit('settings/SET_CUSTOMER_ID', skin.settings.skin_id);
        store.dispatch('user/setCanPlay', geoPlay);

        // B2B authentication (feature-gated), through the skin proxy.
        // When enabled AND the host page passed a `token` attribute, exchange
        // the operator token for a real userId that overrides the one from the
        // HTML attribute — everything downstream (socket, bookietoken, session
        // check) then runs against the resolved user. The proxy also logs the
        // user into Fusio: its token becomes the proxy token of the console
        // (getProxyAccessToken), as for the skin players.
        // On failure the whole bootstrap is aborted with /e/200 (auth error),
        // matching the semantics of a failed bookietoken.
        if (store.getters['settings/getB2bEnabled'] && operatorToken) {
            try {
                const b2bData = await b2bAuthenticate({
                    proxyUrl: store.getters['settings/getProxyUrl'],
                    operatorToken
                });

                const resolvedUserId = Number(b2bData?.userId);
                if (!resolvedUserId || Number.isNaN(resolvedUserId) || resolvedUserId <= 0) {
                    throw new Error(`Invalid userId from B2B auth: ${b2bData?.userId}`);
                }
                if (!b2bData.token) {
                    throw new Error('No proxy token from B2B auth');
                }

                user_id = resolvedUserId;
                startB2bProxySession(b2bData);
                store.dispatch('user/setB2bToken', operatorToken);
            } catch (err) {
                console.error('[B2B] Authentication failed at bootstrap:', err.response?.data || err.message);
                sessionStorage.setItem('_lastAuthErrorStatus', String(err.response?.status || 0));
                // Parent bridge: the partner's token was refused, it can issue a new one.
                postToParent(PARENT_EVENTS.AUTH_FAILED);
                router.push('/e/200');
                mountVue();
                return;
            }
        }

        // Socket
        let socketInstance = null;
        if (process.env.VUE_APP_ENABLE_SOCKET === '1') {
            socketInstance = io(process.env.VUE_APP_URL_SOCKET, {
                transports: ['websocket'],
                upgrade: false,
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 10000,
                timeout: 10000,
            });

            socketInstance.on('connect', () => {
            });
            socketInstance.on('connect_error', (err) => {
            });
            socketInstance.on('disconnect', (reason) => {
                store.commit('socket/SET_SOCKET_ENABLED', false);
            });

            const socket = new VueSocketio({
                debug: false,
                secure: true,
                connection: socketInstance,
                vuex: { store, actionPrefix: 'SOCKET_', mutationPrefix: 'SOCKET_' }
            });
            Vue.use(socket);
            store.commit('socket/setSocket', socket.io);
            store.dispatch('socket/socket_login', { platform_id });
            if (user_id && +user_id !== 0) {
                store.dispatch('socket/socket_login_generic', `${platform_id}-${user_id}`);
            }

            // Re-join platform/user rooms after automatic reconnect.
            socketInstance.on('reconnect', () => {
                store.dispatch('socket/socket_login', { platform_id });
                if (user_id && +user_id !== 0) {
                    store.dispatch('socket/socket_login_generic', `${platform_id}-${user_id}`);
                }
            });

        } else {
            console.warn('[socket] Socket disabled (VUE_APP_ENABLE_SOCKET !== "1")');
        }

        // Single visibilitychange listener: socket reconnect (if enabled) + live data refresh.
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState !== 'visible') return;

            // Re-connect socket if it dropped while the tab was hidden.
            if (socketInstance && !socketInstance.connected) {
                socketInstance.connect();
            }

            // Silent-refresh live data regardless of socket state.
            const livePreviewEvents = store.state.previewLive?.previewLive?.events;
            if (livePreviewEvents && livePreviewEvents.length > 0) {
                store.dispatch('previewLive/silentRefreshLivePreview');
            }
            const liveDetailActive = store.state.detailLive?.eventsLiveDetailActive;
            if (liveDetailActive && liveDetailActive.length > 0) {
                // store.dispatch('detailLive/silentRefreshLiveDetail'); was this old
                store.dispatch('detailLive/silentRefreshLiveDetailV2'); // i am testing this new
            }
        });

        const disableSessionCheck = skin.disableSessionCheck ?? false;

        // User authentication
        if (user_id && +user_id !== 0) {
            if (process.env.NODE_ENV === 'production' && !disableSessionCheck) {
                const checksession = await axios.get('/commons/api.jsp?c=user', {
                    headers: { contentType: 'application/json', Accept: 'application/json' }
                });
                if (!checksession.data.result || checksession.data.data.playerId != user_id) {
                    sessionStorage.setItem('_lastAuthErrorStatus', '401');
                    router.push('/e/100');
                    mountVue();
                    return;
                }
            }

            await axios.post(
                process.env.VUE_APP_URL_API + '/auth/bookietoken',
                { platform: platform_id, userid: +user_id },
                { headers: { Authorization: `Bearer ${token}` } }
            ).then(async ({ data }) => {
                localStorage.setItem('logged_token',   data.accessToken);
                localStorage.setItem('logged_refresh', data.refreshToken);
                localStorage.setItem('logged_expired', data.expired);
                localStorage.setItem('logged_scope',   data.scope);
                localStorage.setItem('logged_type',    data.type);
                localStorage.setItem('logged_cashier',  data.profileType === 'CASHIER' || !!data.cashier);
                localStorage.setItem('logged_terminal', data.profileType === 'TERMINAL');

                store.dispatch('user/setUserData', {
                    id:            data.id,
                    login:         data.login,
                    token:         data.accessToken,
                    type:          data.type,
                    profileType:   data.profileType ?? null,
                    terminal:      data.profileType === 'TERMINAL',
                    cashier:       data.profileType === 'CASHIER' || !!data.cashier,
                    language:      data.language,
                    timezone:      data.timezone,
                    agencyLogin:   data.agencyLogin,
                    operatorLogin: data.operatorLogin,
                    agencyNet:     data.agencyNet,
                    operatorNet:   data.operatorNet
                });

                const showFirstQuickBet = store.getters['settings/getShowQuickbet'];
                const isCashier         = store.getters['user/getCashier'];
                const typeOfUser        = store.getters['user/getType'];
                if (showFirstQuickBet && (isCashier || typeOfUser === 4)) {
                    store.dispatch('ui/setViewMode',    { mode: 'qBet' });
                    store.dispatch('tree/setStatusQbet',  true);
                    store.dispatch('tree/setStatusPrint', false);
                }
            }).catch((error) => {
                // Removed silent catch, added proper error logging for auth failures
                console.error('[initAppVue] Critical error during user login (bookietoken):', error.response?.data || error.message);
                bookietokenFailed = true;
                sessionStorage.setItem('_lastAuthErrorStatus', String(error.response?.status || 0));
                router.push('/e/200');
            });

        } else {
            ['logged_token', 'logged_refresh', 'logged_expired', 'logged_scope'].forEach(k => localStorage.removeItem(k));
            await store.dispatch('user/removeUserData');
        }

        if (icarus !== undefined) window.icarus = true;

        // Electron detection — cashier/agency forced to true
        const isCashier  = store.getters['user/getCashier'];
        const typeOfUser = store.getters['user/getType'];
        const isElectron = detectElectron() || isCashier || typeOfUser === 4;
        store.dispatch('settings/setElectron', isElectron);

        // Enforce section state before Vue mounts so no prematch API is fired
        // from component mounted() hooks during the very first render cycle.
        // Honour the platform-level enableLive flag: if live is disabled at the skin/cart
        // config, ignore the host's openlivesection request — otherwise the user would land
        // on an empty live tree with no clean way back to prematch.
        const isLiveEntry = window.openlivesection === 'true' || window.openlivesection === true;
        const liveEnabled = cartObj?.detail?.enableLive !== false;
        if (isLiveEntry && liveEnabled) {
            store.commit('ui/SET_VIEW_MODE',  'detail');
            store.commit('ui/SET_APP_SECTION', 'live');
        }

        // Only reset the retry counter when the whole bootstrap really
        // succeeded. If bookietoken (or any earlier step marked as failed)
        // did not go through, we leave the counter intact so the error page
        // can honor its bounded-retry budget and eventually stop.
        if (!bookietokenFailed) {
            sessionStorage.removeItem('_errRetry');
            sessionStorage.removeItem('_lastAuthErrorStatus');
        }
        mountVue();

        // Keep the B2B session honest: every minute the proxy token is validated,
        // and every 10 minutes (user/checkB2bToken) /b2b/authenticate runs again,
        // so an operator-side token revocation kicks the user out here as well.
        // Watcher self-stops on invalid; we also clean up the store + redirect.
        // getB2bRefreshEnabled already implies B2B is on; skinning off the flag
        // gives a permanent session (no polling, no refresh).
        if (
            store.getters['settings/getB2bRefreshEnabled'] &&
            store.getters['user/getB2bToken']
        ) {
            startB2bTokenWatcher(store, {
                onInvalid: (result) => {
                    console.warn('[B2B] Token invalidated by watcher:', result?.code);
                    // The error page reloads the console by itself (errorRetryMixin, 3 times at most)
                    // only after a network problem; a session refused by the partner or by the proxy
                    // gets the manual retry button, not another authenticate.
                    sessionStorage.setItem('_lastAuthErrorStatus', result?.transient ? '0' : '401');
                    // Parent bridge: the session is over, the partner can issue a new token.
                    postToParent(PARENT_EVENTS.SESSION_EXPIRED);
                    store.commit('user/SET_B2B_TOKEN', null);
                    clearB2bProxySession();
                    store.dispatch('user/removeUserData');
                    router.push('/e/100');
                }
            });
            window.addEventListener('beforeunload', stopB2bTokenWatcher);
        }

        // Schedule login prompt only for anonymous visitors
        //todo funzionalità settabile loginmessage
        if (loginPromptEnabled && (!user_id || +user_id === 0)) {
            scheduleLoginPrompt(loginPromptDelaySec);
        }

    }).catch(error => {
        console.error('[initAppVue] Bootstrap error:', error);
        sessionStorage.setItem('_lastAuthErrorStatus', String(error.response?.status || 0));
        router.push('/e/300');
        mountVue();
    });
}